import i18next from "i18next"
import type { ListItem } from "../../components/List/List"
import { EComboItemType } from "../../Enum/EComboItemType"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { ComboEntity } from "../Combo/ComboEntity"
import { convertToLocalMoney } from "../Generics/Helps"
import { MemoryClass } from "./MemoryClass"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons"
import { EProductOrderModeType } from "../../Enum/EProductOrderModeType"
import {
  formatReservationDays,
  formatReservationTimes,
  toReservationModeLabel,
} from "../../Extensions/ReservationExtensions"

export class ComboMemory extends MemoryClass<ComboEntity> {
  setList = (comboList: ComboEntity[]) => {
    this.listData = comboList
  }

  protected cloneEntity(item: ComboEntity): ComboEntity {
    return new ComboEntity({ ...item })
  }

  protected getItemId(item: ComboEntity): number {
    return item.id
  }

  getListCards = (all: boolean): ListItem[] => {
    return this.listData
      .filter((combo) => all || combo.catalogProductEntity.canUse())
      .map((combo, index) => this.getCard(combo, index))
  }

  getCard(ss: ComboEntity, count: number): ListItem {
    const catalogProduct = ss.catalogProductEntity

    const enable = catalogProduct.status === EEnableStatusType.Active

    const productItemsCount = ss.items.filter((item) => item.itemType === EComboItemType.Product).length

    const catalogItemItemsCount = ss.items.filter((item) => item.itemType === EComboItemType.CatalogItem).length

    const itemsCount = ss.items.length

    const tagsCount = catalogProduct.tags?.length ?? 0

    const additionalsCount = catalogProduct.allowedAddtionals?.length ?? 0

    const freeAdditionalsCount = catalogProduct.freeAllowedAddtionals?.length ?? 0

    const hasImage = (catalogProduct.iconUrl ?? "").trim().length > 0

    return {
      id: ss.id,
      index: count,
      title: catalogProduct.name,
      linkImage: catalogProduct.iconUrl,
      canUse: catalogProduct.canUse(),
      dataFilter: [
        catalogProduct.name,
        `${itemsCount} item(ns)`,
        `${productItemsCount} produto(ões)`,
        `${catalogItemItemsCount} item complementar(s)`,
      ],
      children: (
        <div
          className={`
          flex justify-between gap-4
          p-4
          rounded-2xl
          transition-all duration-300
          ${enable ? "bg-gradient-to-r from-orange-50 to-white hover:shadow-lg hover:scale-[1.01]" : "bg-gray-100 opacity-70"}
        `}
        >
          <div className="flex gap-4 flex-1 min-w-0">
            {!hasImage && (
              <div className="w-11 h-11 rounded-xl bg-orange-200 text-orange-700 flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
            )}
            <div className="flex flex-col justify-between flex-1 min-w-0">
              <span className="font-extrabold text-gray-900 text-base truncate">{catalogProduct.name}</span>

              <span className="text-sm text-gray-700 line-clamp-2">
                {catalogProduct.description || i18next.t("components:label_no_description")}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end justify-between shrink-0">
            <span className="text-lg font-bold text-gray-800">{convertToLocalMoney(catalogProduct.valueBase)}</span>

            {enable ? (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Ativo</span>
            ) : (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-600">Inativo</span>
            )}
          </div>
        </div>
      ),
      body: (
        <div className="flex gap-2 flex-wrap m-2">
          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700 font-semibold">
            {itemsCount} item(ns)
          </span>

          <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 font-medium">
            🍽️ {productItemsCount}
          </span>

          <span className="px-2 py-1 text-xs rounded-full bg-cyan-100 text-cyan-700 font-medium">
            🥤 {catalogItemItemsCount}
          </span>

          {catalogProduct.promotionsIds?.length > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
              {i18next.t("components:label_promotion")}
            </span>
          )}

          {tagsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">
              🏷️ {tagsCount} tag(s)
            </span>
          )}

          {additionalsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
              ➕ {additionalsCount} adicional(is)
            </span>
          )}

          {freeAdditionalsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-700 font-medium">
              🎁 {freeAdditionalsCount} grátis
            </span>
          )}

          {catalogProduct.needFulfillment && (
            <span className="px-2 py-1 text-xs rounded-full bg-rose-100 text-rose-700 font-medium">👨‍🍳 Expedição</span>
          )}

          {catalogProduct.hasPreparationTime && catalogProduct.preparationTimeMinutes > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
              {i18next.t("components:label_preparation_time_minutes_short", {
                minutes: catalogProduct.preparationTimeMinutes,
              })}
            </span>
          )}

          {catalogProduct.hasStockControl && (
            <span
              className={`px-2 py-1 text-xs rounded-full font-bold
                    ${catalogProduct.stockQuantity > 0 ? "ring-1 ring-orange-300 bg-orange-100 text-orange-500" : "bg-red-100 text-red-600"}`}
            >
              {catalogProduct.stockQuantity > 0 ? `${catalogProduct.stockQuantity} em estoque` : i18next.t("components:label_sold_out")}
            </span>
          )}

          {catalogProduct.category && (
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">
              {catalogProduct.category.name}
            </span>
          )}

          {catalogProduct.productOrderMode !== EProductOrderModeType.ImmediateOnly && (
            <div className="mt-2 w-full border-t border-orange-100 pt-2 text-[11px] font-semibold text-slate-600">
              📅 Reserva: {toReservationModeLabel(catalogProduct.productOrderMode)} • Dias:{" "}
              {formatReservationDays(catalogProduct.reservationAllowedDays)} • Horários:{" "}
              {formatReservationTimes(catalogProduct.reservationAllowedTimes)}
            </div>
          )}
        </div>
      ),
      isDisable: !enable,
    }
  }
}
